import React, { Suspense, lazy, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { GlobalSchemas } from './components/SchemaOrg';
import BackToTop from './components/BackToTop';
import CookieBanner from './components/CookieBanner';
import './App.css';

// Loading component
const PageLoader = () => (
  <div className="min-h-screen bg-[#161616] flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="w-8 h-8 border-2 border-[#B38F6F] border-t-transparent rounded-full animate-spin" />
      <span className="text-[#545454] text-sm">Загрузка...</span>
    </div>
  </div>
);

// Lazy load public pages
const LandingPage = lazy(() => import('./pages/LandingPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const DealPage = lazy(() => import('./pages/DealPage'));
const CostPage = lazy(() => import('./pages/CostPage'));
const NewsPage = lazy(() => import('./pages/NewsPage'));
const NewsArticlePage = lazy(() => import('./pages/NewsArticlePage'));
const ReputationPage = lazy(() => import('./pages/ReputationPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const RequestPage = lazy(() => import('./pages/RequestPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ImportCountryPage = lazy(() => import('./pages/ImportCountryPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const ConfiguratorsPage = lazy(() => import('./pages/ConfiguratorsPage'));
const TuningPage = lazy(() => import('./pages/TuningPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const AIAssistantPage = lazy(() => import('./pages/AIAssistantPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Lazy load admin pages
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminVehicles = lazy(() => import('./pages/admin/VehiclesPage'));
const AdminLeads = lazy(() => import('./pages/admin/LeadsPage'));
const AdminCalcConfig = lazy(() => import('./pages/admin/CalcConfigPage'));
const AdminPayments = lazy(() => import('./pages/admin/PaymentsPage'));

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('fc_token');
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useLayoutEffect(() => {
    if (hash) {
      // Wait briefly for the page to render, then scroll to anchor
      const id = hash.replace('#', '');
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);
  return null;
}

function PageTransition({ children }) {
  const { pathname } = useLocation();
  // `key` forces remount on route change → fade-in plays from top
  return (
    <div key={pathname} className="page-transition">
      {children}
    </div>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <GlobalSchemas />
      <Toaster richColors position="top-right" />
      <div className="App">
        <div className="noise-overlay" />
        <Suspense fallback={<PageLoader />}>
          <PageTransition>
            <Routes>
            {/* Public - New Structure */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/deal" element={<DealPage />} />
            <Route path="/cost" element={<CostPage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/news/:id" element={<NewsArticlePage />} />
            <Route path="/reputation" element={<ReputationPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/request" element={<RequestPage />} />
            <Route path="/login" element={<LoginPage />} />
            
            {/* Import by Country */}
            <Route path="/import/:country" element={<ImportCountryPage />} />
            
            {/* FAQ */}
            <Route path="/faq" element={<FAQPage />} />
            
            {/* Configurators */}
            <Route path="/configurators" element={<ConfiguratorsPage />} />
            
            {/* Tuning */}
            <Route path="/tuning" element={<TuningPage />} />
            
            {/* About */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/ai" element={<AIAssistantPage />} />

            {/* Legacy redirects */}
            <Route path="/customs" element={<Navigate to="/cost" replace />} />
            <Route path="/calculator" element={<Navigate to="/cost" replace />} />
            <Route path="/sources" element={<Navigate to="/search" replace />} />
            <Route path="/cars" element={<Navigate to="/search" replace />} />

            {/* Admin */}
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/vehicles" element={<ProtectedRoute><AdminVehicles /></ProtectedRoute>} />
            <Route path="/admin/leads" element={<ProtectedRoute><AdminLeads /></ProtectedRoute>} />
            <Route path="/admin/calculator-config" element={<ProtectedRoute><AdminCalcConfig /></ProtectedRoute>} />
            <Route path="/admin/payments" element={<ProtectedRoute><AdminPayments /></ProtectedRoute>} />
            
            {/* 404 - Must be last */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          </PageTransition>
        </Suspense>
        <BackToTop />
        <CookieBanner />
      </div>
    </Router>
  );
}

export default App;
;
